"use client";

import { ToolCallMessagePartComponent, useAui } from "@assistant-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ClientLogger } from "@/lib/clientLogger";
import { submitToolCallResult } from "./ask-submit";
import { StepperFooter, StepperHeader } from "./stepper-shell";
import { useToolCardGating } from "./use-tool-card-gating";
import { useStepperNavigation } from "./use-stepper-navigation";

// Mirror of backend AskOpenTool args shape (src/backend/AiAssistant.Api/Services/Tools/AskOpenTool.cs).
type AskOpenQuestion = {
  id: string;
  header: string;
  question: string;
};

type AskOpenArgs = { questions: AskOpenQuestion[] };

type AskOpenAnswer = {
  id: string;
  text: string | null;
};

/**
 * Ask-open card — cut-turn flow for 1–4 open-ended questions rendered as a stepper.
 *
 * Each step exposes a Skip button alongside Next/Submit. A skipped question carries a
 * null `text` in the wire payload and renders as `(skipped)` in the next-turn user message.
 * The /chat/tool-call/{id}/result endpoint determines the sentinel ("User answered" if any
 * step has text, "User skipped" otherwise) — the frontend only POSTs the structured
 * answers array.
 *
 * Refresh behaviour mirrors AskCard:
 * - Mid-card (no submit yet): tool-call part has empty `result` → card re-renders fresh.
 * - After submit: the submit endpoint writes the sentinel onto the persisted part's content,
 *   so `result` is non-empty → card renders null and only the Q/A user message remains visible.
 */
export const AskOpenCard: ToolCallMessagePartComponent = ({
  toolCallId,
  argsText,
  status,
}) => {
  const { t } = useTranslation();
  const gating = useToolCardGating<AskOpenArgs>(
    toolCallId,
    argsText,
    status,
    (args) => (args.questions?.length ?? 0) > 0,
  );

  if (gating.state === "hidden") return null;
  if (gating.state === "loading") {
    return (
      <div className="aui-ask-open-card-loading rounded-lg bg-background px-4 py-3 text-sm text-muted-foreground">
        {t("askOpen.loading")}
      </div>
    );
  }

  return (
    <AskOpenForm
      toolCallId={toolCallId}
      args={gating.args}
      onSubmitted={gating.onSubmitted}
    />
  );
};

// ─── Stepper form ────────────────────────────────────────────────────────────

const AskOpenForm: React.FC<{
  toolCallId: string;
  args: AskOpenArgs;
  onSubmitted: () => void;
}> = ({ toolCallId, args, onSubmitted }) => {
  const aui = useAui();
  const { t } = useTranslation();
  const {
    stepIndex, setStepIndex, current, total, goBack, goNext, isLast,
    submitting, setSubmitting, errorMessage, setErrorMessage,
  } = useStepperNavigation(args.questions);
  const [texts, setTexts] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const q of args.questions) init[q.id] = "";
    return init;
  });
  const [skipped, setSkipped] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const q of args.questions) init[q.id] = false;
    return init;
  });

  const curText = texts[current.id] ?? "";
  const curSkipped = skipped[current.id] ?? false;

  const setCurrentText = useCallback((text: string) => {
    setTexts(prev => ({ ...prev, [current.id]: text }));
    if (text.length > 0)
      setSkipped(prev => ({ ...prev, [current.id]: false }));
  }, [current.id]);

  const skipCurrent = useCallback(() => {
    setSkipped(prev => ({ ...prev, [current.id]: true }));
    setTexts(prev => ({ ...prev, [current.id]: "" }));
  }, [current.id]);

  // A step is "decided" if the user typed something OR explicitly skipped — both unblock Next.
  const isDecided = useCallback(
    (qid: string) => (texts[qid] ?? "").trim().length > 0 || (skipped[qid] ?? false),
    [skipped, texts],
  );
  const canAdvance = isDecided(current.id);
  const canSubmit = useMemo(
    () => args.questions.every(q => isDecided(q.id)),
    [args.questions, isDecided],
  );

  // Per-question Q:/A: prose. Skipped questions render `(skipped)` so the model retains
  // fidelity about which it answered vs. which it didn't.
  const buildProse = useCallback((): string => {
    const blocks: string[] = [];
    for (const q of args.questions) {
      const trimmed = (texts[q.id] ?? "").trim();
      const skip = skipped[q.id] ?? false;
      const answer = !skip && trimmed.length > 0 ? `"${trimmed}"` : t("askOpen.skippedMarker");
      blocks.push(`Q: ${q.question}\nA: ${answer}`);
    }
    return blocks.join("\n\n");
  }, [args.questions, skipped, t, texts]);

  const onSubmit = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setErrorMessage(null);

    try {
      const answers: AskOpenAnswer[] = args.questions.map(q => {
        const trimmed = (texts[q.id] ?? "").trim();
        const skip = skipped[q.id] ?? false;
        return { id: q.id, text: !skip && trimmed.length > 0 ? trimmed : null };
      });

      const res = await submitToolCallResult(toolCallId, { answers });
      if (res.kind === "stale") {
        ClientLogger.warn("AskOpen submit returned stale (410)", {
          component: "AskOpenCard",
          action: "submitToolCallResult",
          metadata: { toolCallId, questionCount: args.questions.length },
        });
        throw new Error(t("askOpen.error409"));
      }
      if (res.kind === "error") {
        ClientLogger.error("AskOpen submit POST failed", {
          component: "AskOpenCard",
          action: "submitToolCallResult",
          metadata: {
            toolCallId,
            status: res.status,
            questionCount: args.questions.length,
          },
        });
        if (res.status === 409) throw new Error(t("askOpen.error409"));
        throw new Error(t("askOpen.submitFailed", { status: String(res.status) }));
      }

      onSubmitted();

      // Hybrid in-loop wait: if the server signalled continuation, the agent loop keeps
      // running in the same turn — the SSE stream is still open, text-delta events will
      // continue. Do NOT append a user message or start a new /chat turn.
      if (res.continuation?.continuation) {
        ClientLogger.info("Tool-call result accepted (continuation)", {
          component: "AskOpenCard",
          action: "submitResult",
          metadata: { toolCallId, continuation: true },
        });
        return;
      }

      ClientLogger.info("Tool-call result accepted (new turn)", {
        component: "AskOpenCard",
        action: "submitResult",
        metadata: { toolCallId, continuation: false },
      });

      const prose = buildProse();
      aui.thread().append({
        role: "user",
        content: [{ type: "text", text: prose }],
      });
    } catch (err) {
      ClientLogger.error("AskOpen submit threw", {
        error: err instanceof Error ? err : undefined,
        component: "AskOpenCard",
        action: "onSubmit",
        metadata: { toolCallId, questionCount: args.questions.length },
      });
      setErrorMessage((err as Error).message);
      setSubmitting(false);
    }
  }, [args.questions, aui, buildProse, canSubmit, onSubmitted, skipped, submitting, t, texts, toolCallId]);

  return (
    <div className="aui-ask-open-card flex w-full flex-col gap-4 rounded-lg bg-background p-4 text-sm">
      <StepperHeader
        show={total > 1}
        counterLabel={t("askOpen.stepIndicator", { current: stepIndex + 1, total })}
        disabled={submitting}
        dots={args.questions.map((q, i) => ({
          key: q.id,
          ariaLabel: `${q.header} (${i + 1}/${total})`,
          state: i === stepIndex
            ? "active"
            : isDecided(q.id)
              ? "done"
              : "pending",
          onClick: () => setStepIndex(i),
        }))}
      />

      <section key={current.id} className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
            {current.header}
          </span>
          <p className="font-medium text-foreground">{current.question}</p>
        </div>

        <textarea
          rows={4}
          autoFocus
          value={curText}
          onChange={e => setCurrentText(e.target.value)}
          disabled={submitting || curSkipped}
          placeholder={curSkipped ? t("askOpen.skippedPlaceholder") : t("askOpen.placeholder")}
          className="w-full resize-none rounded border bg-background px-2 py-1 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20 disabled:opacity-60"
          onKeyDown={e => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              if (isLast) void onSubmit();
              else if (canAdvance) goNext();
            }
          }}
        />
      </section>

      {errorMessage && <p className="text-xs text-destructive">{errorMessage}</p>}

      <StepperFooter
        back={{
          label: t("askOpen.back"),
          onClick: goBack,
          disabled: stepIndex === 0 || submitting,
        }}
        middle={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={skipCurrent}
            disabled={submitting || curSkipped}
          >
            {t("askOpen.skip")}
          </Button>
        }
        primary={isLast
          ? {
              label: submitting ? t("askOpen.submitting") : t("askOpen.submit"),
              onClick: onSubmit,
              disabled: !canSubmit || submitting,
            }
          : {
              label: t("askOpen.next"),
              onClick: goNext,
              disabled: !canAdvance || submitting,
            }}
      />
    </div>
  );
};
